import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../../../ui/card";
import { Button } from "../../../ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../../ui/accordion";
import {
  AlertTriangle,
  ArrowLeft,
  BookOpenCheck,
  Calculator,
  CheckCircle,
  ChevronUp,
  Eye,
  ShieldCheck,
} from "lucide-react";
import { FormData, RoomAvailability } from "types/instantBookingTypes";
import { LawazimConfig, RoomTypeCharges } from "types/lawazimConfig";
import {
  PortApi,
  PropertyApi,
  getProperty,
  getBookingCharges,
  BookingCharge,
  BookingCancellationTier,
} from "services/tourService";
import { FullScreenLoading } from "components/ui/loader";

interface ConfirmationProps {
  formData: FormData;
  roomAvailability: RoomAvailability[];
  ports: PortApi[];
  termsAndConditions?: string;
  lawazimConfigs?: LawazimConfig[];
  properties: PropertyApi[];

  expandedRoomDetails: string | null;
  setExpandedRoomDetails: React.Dispatch<React.SetStateAction<string | null>>;

  getTotalRequestedCapacity: () => {
    min: number;
    max: number;
  };

  getVenueName: (venueId: number | null) => string;

  handleFinalConfirmation: () => void;

  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
}

type SelectedRoomCharge = {
  room: RoomAvailability;
  nights: number;
  lawazim?: LawazimConfig;
  charge?: RoomTypeCharges;
  basePrice: number;
  roomCharge: number;
};

// const formatAmount = (amount: number) =>
//   new Intl.NumberFormat("en-IN", {
//     style: "currency",
//     currency: "INR",
//     maximumFractionDigits: 0,
//   }).format(amount || 0);

const getNights = (checkInDate: string, checkOutDate: string) => {
  if (!checkInDate || !checkOutDate) return 1;

  const nights = Math.ceil(
    (new Date(checkOutDate).getTime() - new Date(checkInDate).getTime()) /
      (1000 * 60 * 60 * 24),
  );

  return Math.max(1, nights);
};

const textEquals = (left?: string | number, right?: string | number) =>
  String(left || "")
    .trim()
    .toLowerCase() ===
  String(right || "")
    .trim()
    .toLowerCase();

const findLawazimCharge = (
  room: RoomAvailability,
  lawazimConfigs: LawazimConfig[],
) => {
  for (const lawazim of lawazimConfigs) {
    const roomTypeCharges = lawazim.roomTypeCharges || [];

    const exactCharge = roomTypeCharges.find((charge) => {
      const roomTypeMatches =
        textEquals(charge.roomTypeId, room.roomType) ||
        textEquals(charge.roomTypeName, room.roomTypeName) ||
        textEquals(charge.roomTypeCode, room.roomType);

      const propertyMatches =
        !charge.propertyId ||
        textEquals(charge.propertyId, room.propertyId) ||
        textEquals(charge.propertyName, room.propertyName);

      return roomTypeMatches && propertyMatches;
    });

    if (exactCharge) return { lawazim, charge: exactCharge };

    const roomOnlyCharge = roomTypeCharges.find(
      (charge) =>
        textEquals(charge.roomTypeId, room.roomType) ||
        textEquals(charge.roomTypeName, room.roomTypeName) ||
        textEquals(charge.roomTypeCode, room.roomType),
    );

    if (roomOnlyCharge) return { lawazim, charge: roomOnlyCharge };
  }

  return { lawazim: undefined, charge: undefined };
};

export default function Confirmation({
  formData,
  roomAvailability,
  ports,
  properties,
  termsAndConditions,
  lawazimConfigs = [],
  expandedRoomDetails,
  setExpandedRoomDetails,
  getTotalRequestedCapacity,
  getVenueName,
  handleFinalConfirmation,
  setCurrentStep,
}: ConfirmationProps) {
  const [hasAcceptedTerms, setHasAcceptedTerms] = React.useState(false);
  const [loadingCharges, setLoadingCharges] = useState(false);

  const [apiCharges, setApiCharges] = useState<
    {
      propertyPublicId: string;
      propertyRoomTypePublicId: string;
      charge: BookingCharge;
    }[]
  >([]);

  const [, setCancellationPolicies] = useState([]);
  const [cancellationTiers, setCancellationTiers] = useState<
    BookingCancellationTier[]
  >([]);

  useEffect(() => {
    const loadCharges = async () => {
      try {
        setLoadingCharges(true);
        const selectedRooms = roomAvailability.filter((r) => r.requested > 0);

        const venues = [];

        for (const room of selectedRooms) {
          const property = properties.find(
            (p) => p.propertyId === Number(room.propertyId),
          );

          if (!property) continue;

          const detail = await getProperty(property.publicId);

          const roomType = detail.roomTypes.find(
            (r) => r.propertyRoomTypeId === Number(room.roomType),
          );

          if (!roomType) continue;

          venues.push({
            venueRowNo: venues.length + 1,

            propertyPublicId: detail.publicId,

            checkInDate: room.checkInDate,

            checkInTime: detail.checkInTime,

            checkOutDate: room.checkOutDate,

            checkOutTime: detail.checkOutTime,

            rooms: [
              {
                roomSequence: 1,
                propertyRoomTypePublicId: roomType.publicId,
                quantity: room.requested,
              },
            ],
          });
        }

        if (!venues.length) {
          setApiCharges([]);
          return;
        }

        const response = await getBookingCharges({
          chargeCalculationMode: 1,
          cancellationScope: 0,
          tourId: 0,

          venues,

          propertyTypes: [],
          transport: [],
          interVenue: [],
          atraaf: [],
          additionalServices: [],
        });

        setCancellationPolicies([]);
        setCancellationTiers([...response.cancellationTiers]);

        const mapped = response.charges.map((charge) => ({
          propertyPublicId: charge.propertyPublicId,
          propertyRoomTypePublicId: charge.propertyRoomTypePublicId,
          charge,
        }));

        setApiCharges(mapped);
      } catch (error) {
        console.error(error);
      } finally {
        setLoadingCharges(false);
      }
    };

    loadCharges();
  }, [roomAvailability, properties]);

  const selectedRoomCharges = React.useMemo<SelectedRoomCharge[]>(() => {
    return roomAvailability
      .filter((room) => room.requested > 0)
      .map((room) => {
        const { lawazim, charge } = findLawazimCharge(room, lawazimConfigs);
        const nights = getNights(room.checkInDate, room.checkOutDate);
        const basePrice = Number(charge?.basePrice || 0);
        const isFixedRate = String(lawazim?.chargeType || "")
          .toLowerCase()
          .includes("fixed");
        const roomCharge =
          basePrice * room.requested * (isFixedRate ? 1 : nights);

        return {
          room,
          nights,
          lawazim,
          charge,
          basePrice,
          roomCharge,
        };
      });
  }, [lawazimConfigs, roomAvailability]);

  return (
    <>
      {loadingCharges && <FullScreenLoading />}
      <div className="space-y-5">
        <div
          className="grid grid-cols-1 gap-4 xl:grid-cols-2"
          style={{ padding: "0px" }}
        >
          <Card className="bg-white w-full h-full" style={{ marginTop: "0px" }}>
            <CardContent
              className="[&:last-child] pt-2"
              style={{ paddingBottom: "4px" }}
            >
              <div className="space-y-2">
                {/* Tour Name */}
                <div className="flex justify-between py-2 border-b">
                  <span
                    className="text-sm font-medium text-gray-600"
                    style={{ width: "50%" }}
                  >
                    Tour Name
                  </span>

                  <span
                    className="text-xs text-gray-900 capitalize"
                    style={{ width: "50%" }}
                  >
                    {formData.tourName}
                  </span>
                </div>

                {/* Duration */}
                <div className="flex justify-between py-2 border-b">
                  <span
                    className="text-sm font-medium text-gray-600"
                    style={{ width: "50%" }}
                  >
                    Duration
                  </span>

                  <span
                    className="text-xs text-gray-900"
                    style={{ width: "50%" }}
                  >
                    {formData.arrivalDate && formData.departureDate
                      ? `${
                          Math.ceil(
                            (new Date(formData.departureDate).getTime() -
                              new Date(formData.arrivalDate).getTime()) /
                              (1000 * 60 * 60 * 24),
                          ) + 1
                        } Days`
                      : "-"}
                  </span>
                </div>

                {/* Room Details */}
                <div className="flex justify-between py-2 border-b">
                  <span
                    className="text-sm font-medium text-gray-600"
                    style={{ width: "50%" }}
                  >
                    Room Details
                  </span>

                  <div className="space-y-2" style={{ width: "50%" }}>
                    {Object.entries(
                      roomAvailability
                        .filter((room) => room.requested > 0)
                        .reduce(
                          (acc, room) => {
                            if (!acc[room.propertyName]) {
                              acc[room.propertyName] = [];
                            }

                            acc[room.propertyName].push(room);

                            return acc;
                          },
                          {} as Record<string, typeof roomAvailability>,
                        ),
                    ).map(([propertyName, rooms]) => {
                      const isExpanded = expandedRoomDetails === propertyName;

                      return (
                        <div key={propertyName}>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div className="text-xs font-semibold text-gray-900">
                              {propertyName}
                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                setExpandedRoomDetails(
                                  isExpanded ? null : propertyName,
                                )
                              }
                              className="
                                h-8 w-8
                                flex items-center justify-center
                                rounded-md
                                bg-gray-50
                                hover:bg-blue-100
                                transition-colors
                              "
                            >
                              {isExpanded ? (
                                <ChevronUp className="h-4 w-4 text-blue-600" />
                              ) : (
                                <Eye className="h-4 w-4 text-blue-600" />
                              )}
                            </button>
                          </div>

                          {isExpanded && (
                            <div className="space-y-1 mt-2">
                              {rooms.map((room, idx) => (
                                <div
                                  key={idx}
                                  className="grid grid-cols-1 sm:grid-cols-[125px_1fr] text-xs text-gray-600 gap-1"
                                >
                                  <span>{room.roomTypeName}</span>

                                  <span>
                                    {room.requested} room
                                    {room.requested > 1 ? "s" : ""}
                                    {" • "}
                                    {room.requested * room.minCapacity}-
                                    {room.requested * room.maxCapacity}
                                    pax
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Applicants */}
                <div className="flex justify-between py-2 border-b">
                  <span
                    className="text-sm font-medium text-gray-600"
                    style={{ width: "50%" }}
                  >
                    Applicants
                  </span>

                  <span
                    className="text-xs text-gray-900"
                    style={{ width: "50%" }}
                  >
                    {formData.noOfApplicants} Applicants
                  </span>
                </div>

                {/* Capacity */}
                <div className="flex justify-between py-2 border-b">
                  <span
                    className="text-sm font-medium text-gray-600"
                    style={{ width: "50%" }}
                  >
                    Total Room Capacity
                  </span>

                  <span
                    className="text-xs text-gray-900"
                    style={{ width: "50%" }}
                  >
                    {getTotalRequestedCapacity().min} -
                    {getTotalRequestedCapacity().max} pax
                  </span>
                </div>

                {/* Arrival */}
                <div className="flex justify-between py-2 border-b">
                  <span
                    className="text-sm font-medium text-gray-600"
                    style={{ width: "50%" }}
                  >
                    Arrival
                  </span>

                  <div style={{ width: "50%" }}>
                    <div className="text-xs text-gray-900">
                      {formData.arrivalDate} •{" "}
                      {
                        ports.find((p) => p.portId === formData.arrivalAirport)
                          ?.portName
                      }
                    </div>

                    <div className="text-xs text-gray-600 mt-0.5">
                      Flight {formData.arrivalFlightNumber} •{" "}
                      {formData.arrivalTime}
                    </div>
                  </div>
                </div>

                {/* Departure */}
                <div className="flex justify-between py-2 border-b">
                  <span
                    className="text-sm font-medium text-gray-600"
                    style={{ width: "50%" }}
                  >
                    Departure
                  </span>

                  <div style={{ width: "50%" }}>
                    <div className="text-xs text-gray-900">
                      {formData.departureDate} •{" "}
                      {
                        ports.find(
                          (p) => p.portId === formData.departureAirport,
                        )?.portName
                      }
                    </div>

                    <div className="text-xs text-gray-600 mt-0.5">
                      Flight {formData.departureFlightNumber} •{" "}
                      {formData.departureTime}
                    </div>
                  </div>
                </div>

                {/* Venues */}
                <div className="flex justify-between py-2">
                  <span
                    className="text-sm font-medium text-gray-600"
                    style={{ width: "50%" }}
                  >
                    Venues
                  </span>

                  <div className="space-y-1" style={{ width: "50%" }}>
                    {formData.venues.map((venue, index) => (
                      <div key={index} className="text-xs text-gray-900">
                        {getVenueName(venue.venueId)} • (
                        {venue.fromDate && venue.toDate
                          ? Math.max(
                              1,
                              Math.ceil(
                                (new Date(venue.toDate).getTime() -
                                  new Date(venue.fromDate).getTime()) /
                                  (1000 * 60 * 60 * 24),
                              ),
                            )
                          : 0}{" "}
                        nights)
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Accordion
            type="multiple"
            defaultValue={["cancellation-policy"]}
            className="w-full h-full"
          >
            <AccordionItem
              value="cancellation-policy"
              className="h-full overflow-hidden rounded-xl border border-amber-200 bg-white shadow-sm"
            >
              <div className="px-5 pt-4 pb-1">
                <div className="flex items-start gap-3 text-left">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600 shadow-sm">
                    <Calculator className="h-5 w-5" />
                  </span>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Cancellation Policy
                    </h2>
                    <p className="mt-1 text-xs font-normal text-gray-600 mb-2">
                      Review Lawazim charges before confirming this booking.
                    </p>
                  </div>
                </div>
              </div>
              <AccordionContent style={{ padding: "0px" }}>
                <Card
                  className="border-0 shadow-none"
                  style={{ marginTop: "0px" }}
                >
                  <CardContent style={{ padding: 0 }}>
                    <div className="space-y-4 pt-1 px-4">
                      {selectedRoomCharges.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
                          Select rooms in Available Rooms to view cancellation
                          charges.
                        </div>
                      ) : (
                        <>
                          {apiCharges.some((c) => !c.charge?.lawazimName) && (
                            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                              Some selected rooms do not have cancellation
                              charges configured.
                            </div>
                          )}

                          {/* <div className="overflow-hidden rounded-lg border border-gray-200">
                            <div className="hidden grid-cols-[1.6fr_1fr_1fr] gap-3 bg-gray-50 px-4 py-3 text-xs font-semibold text-gray-600 md:grid">
                              <span>Selected room</span>
                              <span>Base price</span>
                              <span>Total room charge</span>
                            </div>
                            {selectedRoomCharges.map((item) => {
                              const charge = apiCharges.find(
                                (c) =>
                                  c.propertyId === Number(item.room.propertyId) &&
                                  c.propertyRoomTypeId ===
                                    Number(item.room.roomType),
                              )?.charge;

                              return (
                                <div
                                  key={`${item.room.propertyId}-${item.room.roomType}-${item.room.checkInDate}`}
                                  className="grid grid-cols-1 gap-2 border-t px-4 py-3 text-sm md:grid-cols-[1.6fr_1fr_1fr] md:gap-3"
                                >
                                  <div>
                                    <p className="font-semibold text-gray-900">
                                      {item.room.propertyName} -{" "}
                                      {item.room.roomTypeName}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {item.room.requested} room(s), {item.nights}{" "}
                                      night(s)
                                    </p>
                                  </div>
                                  <p className="font-medium text-gray-900">
                                    {formatAmount(charge?.basePrice ?? 0)}
                                  </p>
                                  <p className="font-semibold text-[#1f3d5a]">
                                    {formatAmount(charge?.totalCharge ?? 0)}
                                  </p>
                                </div>
                              );
                            })}
                          </div> */}

                          <div className="rounded-lg border border-sky-100 bg-sky-50/50 p-4">
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">
                              Cancellation Tiers
                            </h3>
                            <div className="max-h-[300px] overflow-y-auto w-full overflow-x-hidden rounded-lg border border-gray-200">
                              <div className="hidden grid-cols-[2fr_2fr_1fr] gap-3 bg-gray-50 px-4 py-3 text-xs font-semibold text-gray-600 md:grid">
                                <span>Days Before Check-In</span>
                                <span>Based On</span>
                                <span>Penalty</span>
                              </div>
                              
                              {cancellationTiers.length > 0 ? (
                                cancellationTiers.map((tier, index) => (
                                  <div
                                    key={index}
                                    className="grid grid-cols-[2fr_2fr_1fr] border-t px-4 py-3 text-sm"
                                  >
                                    <span>{tier.daysBeforeCheckIn} Days</span>

                                    <span className="capitalize">
                                      {tier.basedOnDisplayName}
                                    </span>

                                    <span className="font-semibold text-amber-700">
                                      {tier.penaltyValue}
                                    </span>
                                  </div>
                                ))
                              ) : (
                                <div className="px-4 py-3 text-sm text-gray-500">
                                  No cancellation tiers found.
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <Accordion
          type="multiple"
          // defaultValue={["terms"]}
          className="w-full space-y-4"
        >
          <AccordionItem
            value="terms"
            className="overflow-hidden rounded-xl border border-sky-200 bg-white shadow-sm"
          >
            <AccordionTrigger className="px-5 py-4 hover:no-underline [&>svg]:mt-3">
              <div className="flex w-full flex-col gap-3 pr-3 text-left sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#5B9BD5]/10 text-[#5B9BD5]">
                    <BookOpenCheck className="h-5 w-5" />
                  </span>
                  <div>
                    <h2 className="text-base font-semibold text-gray-900">
                      Terms & Conditions{" "}
                      <span className="text-red-500 ml-1">*</span>
                    </h2>
                    <p className="mt-1 text-xs font-normal text-gray-600">
                      Review the complete terms, then tick the confirmation
                      below.
                    </p>
                  </div>
                </div>
                {/* <Badge className="w-fit shrink-0 border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50">
                  Required before submit
                </Badge> */}
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5 pt-0">
              <div className="max-h-[360px] overflow-y-auto rounded-lg border border-gray-200 bg-white p-4 shadow-inner">
                {termsAndConditions ? (
                  <div
                    className="prose prose-sm max-w-none text-gray-700 [&_*]:max-w-full"
                    dangerouslySetInnerHTML={{
                      __html: termsAndConditions,
                    }}
                  />
                ) : (
                  <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-500">
                    Terms and conditions are not available right now.
                  </div>
                )}
              </div>

              <label className="mt-4 flex cursor-pointer items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50/70 p-4 transition hover:bg-emerald-50">
                <input
                  type="checkbox"
                  checked={hasAcceptedTerms}
                  onChange={(event) =>
                    setHasAcceptedTerms(event.target.checked)
                  }
                  className="mt-1 h-4 w-4 rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="flex-1">
                  <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                    <ShieldCheck className="h-4 w-4 text-emerald-600" />I have
                    read the terms and confirm the tour details are accurate and
                    complete
                  </span>
                  <span className="mt-1 block text-xs text-gray-600">
                    Check this only after reviewing the notes, tour summary, and
                    terms above.
                  </span>
                </span>
              </label>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {/* Footer */}
        <div className="flex items-center justify-between mt-5">
          <Button
            variant="outline"
            onClick={() => setCurrentStep(3)}
            style={{ background: "#5B9BD5" }}
            className="text-white text-xs h-9"
          >
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" />
            Previous
          </Button>

          <Button
            onClick={handleFinalConfirmation}
            disabled={!hasAcceptedTerms}
            className="bg-green-600 hover:bg-green-700 text-white text-xs h-9 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            <CheckCircle className="w-3.5 h-3.5 mr-1.5" />
            Confirm Booking
          </Button>
        </div>
      </div>
    </>
  );
}
